import { Fragment, lazy, useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Row, Col, UncontrolledTooltip, Alert } from 'reactstrap';

import PanelCollapse from 'src/componentes/panel-collapse';
import RiscoPontoControleEdit from './RiscoPontoControleEditView';
import List from 'src/componentes/list/tree';
import ListColumn from 'src/componentes/list/list-column';
import { capitalizeFirstLetter } from 'src/utils/string';
import LucideIcon from 'src/componentes/lucide-icon';
import Legenda from 'src/paginas/gestao-risco/legenda-farol-revisao';
import CausasRisco from './causas-risco';
import { COLUNAS, TIPO_INTERACAO_SOPHIE, MODULOS } from 'src/utils/constants';
import ContentManager from 'src/componentes/content-manager';
import { getConfiguration } from 'src/services/commonRequests';
import Button from 'src/componentes/button';
import http from 'src/services/httpService';
import errorHandler from 'src/utils/errorHandler';

import ConsequenciasRisco from './consequencias-risco';
import GraficoBowTieView from '../../componentes/GraficoBowTieView';
import EfetividadeControle from './risco-controle/components/AvaliacaoEfetividadeControle';
import LoadingContainer from 'src/componentes/loading-container';
import ValidarRestricaoNovo from 'src/componentes/validar-restricao-novo/ValidarRestricao';
import { formatNumberWithSeparators } from 'src/utils/number';
import { SophieIcon } from 'src/componentes/icons';
import useAuth from 'src/hooks/useAuth';

const AlertMessageBox = lazy(() => import('src/componentes/message-box/alert'));
const AssistenteVirtual = lazy(() =>
  import('src/components/IA/ChatGPT/AssistenteVirtual')
);

const RiscoPontoControleList = (props) => {
  const intl = useIntl();
  const {
    terms: resources,
    decimalSeparator,
    thousandsSeparator,
    user,
    modules,
  } = useAuth();

  const {
    model,
    disabled = false,
    showPaginacaoSimples,
    pageSize,
    onCausasUpdated,
    onConsequenciasUpdated,
    modelOrigem,
    showEfetividadeControlePanel,
    disabledEfetividadeControlePanel,
    requiredEfetividadeControlePanel,
    modelCorretivo,
    modelPreventivo,
    configuracaoAvaliacao,
    efetividadeAutomatica,
    causasRisco: causasRiscoProp,
    consequenciasRisco: consequenciasRiscoProp,
    riscoId: riscoIdProp,
    configuracao: configuracaoRiscoProp,
    handleClose,
  } = props;

  const [saved, setSaved] = useState(-1);
  const [configuracao, setConfiguracao] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [pesoControle, setPesoControle] = useState(null);
  const [isLoadingPesoControle, setIsLoadingPesoControle] = useState(true);
  const [causasRisco, setCausasRisco] = useState(causasRiscoProp || null);
  const [consequenciasRisco, setConsequenciasRisco] = useState(
    consequenciasRiscoProp || null
  );

  const riscoId = riscoIdProp || model.getValue('id');

  useEffect(() => {
    getConfiguration('PontoControleConfiguracao')((configuracaoRecebida) => {
      setConfiguracao(configuracaoRecebida);
      setIsLoading(false);
    })();
  }, []);

  const obterPesoPorFuncaoControle = (idRisco) => {
    if (!idRisco) return;

    try {
      http
        .post('/RiscoPontoControle/ObterPesoControleRisco', {
          idRisco: idRisco,
        })
        .then((response) => {
          if (response?.data) setPesoControle(response.data);
        });
    } catch (err) {
      errorHandler(err);
    } finally {
      setIsLoadingPesoControle(false);
    }
  };

  const obterCausasAtualizadas = (idRisco) => {
    if (!idRisco) return;

    try {
      http
        .post('/RiscoCausaControle/ObterCausasRiscoAtualizadas', {
          idRisco: idRisco,
        })
        .then((response) => {
          if (response?.data) setCausasRisco(response.data);

          if (onCausasUpdated) onCausasUpdated(response.data);
        });
    } catch (err) {
      errorHandler(err);
    }
  };

  const obterConsequenciasAtualizadas = (idRisco) => {
    if (!idRisco) return;

    try {
      http
        .post('/RiscoConsequenciaControle/ObterConsequenciasRiscoAtualizadas', {
          idRisco: idRisco,
        })
        .then((response) => {
          if (response?.data) setConsequenciasRisco(response.data);

          if (onConsequenciasUpdated) onConsequenciasUpdated(response.data);
        });
    } catch (err) {
      errorHandler(err);
    }
  };

  const forceRefresh = () => {
    setSaved((prev) => prev + 1);
    setIsLoadingPesoControle(true);
    obterPesoPorFuncaoControle(riscoId);
    obterCausasAtualizadas(riscoId);
    obterConsequenciasAtualizadas(riscoId);
  };

  const parsePeso = (value) => {
    if (value == null || value === '') return 0;

    const numberValue = Number(value);
    if (!isNaN(numberValue)) return numberValue;

    let normalizedValue = value.toString();
    if (thousandsSeparator) {
      normalizedValue = normalizedValue.split(thousandsSeparator).join('');
    }
    if (decimalSeparator) {
      normalizedValue = normalizedValue.split(decimalSeparator).join('.');
    }

    const parsedValue = Number(normalizedValue);
    return isNaN(parsedValue) ? 0 : parsedValue;
  };

  const formatPeso = (value) =>
    formatNumberWithSeparators(value, 2, decimalSeparator, thousandsSeparator);

  const habilitadoEfetividadePonderada =
    configuracao?.avaliarEfetividadeControle &&
    configuracao?.calcularEfetividadeConformeEfetividadeIndividual &&
    configuracao?.calcularEfetividadeGeralFormaPonderada;

  const pesoHabilitado =
    configuracao?.avaliarEfetividadeControle &&
    configuracao?.avaliarEfetividadeControleIndividualmente &&
    configuracao?.calcularEfetividadeConformeEfetividadeIndividual &&
    configuracao?.calcularEfetividadeGeralFormaPonderada;

  useEffect(() => {
    if (habilitadoEfetividadePonderada && isLoadingPesoControle) {
      obterPesoPorFuncaoControle(riscoId);
    }
  }, [habilitadoEfetividadePonderada, isLoadingPesoControle, riscoId]);

  const handleEditClick = (item, parameters) => {
    ContentManager.addContent(
      <RiscoPontoControleEdit
        item={item}
        currenttabIndex={0}
        parameters={parameters}
        afterSave={forceRefresh}
        disabled={disabled}
        handleClose={handleClose}
      />
    );
  };

  const handleDeleteClickConfirm = (excluido) => {
    if (excluido) {
      ContentManager.addContent(
        <AlertMessageBox
          title={intl.formatMessage({ id: 'sucesso' })}
          message={intl.formatMessage({
            id: 'label.registroExcluidoComSucesso',
          })}
        />
      );

      forceRefresh();
    }
  };

  const handleDelete = (item) => {
    ContentManager.addContent(
      <ValidarRestricaoNovo
        title="labelExcluirVinculoControleRisco"
        description="labelExcluirVinculoControleRiscoDescricao"
        subDescription="labelExcluirVinculoControleRiscoDescricao2"
        url="/RiscoPontoControle/ValidarRestricoes"
        urlExclusao="/RiscoPontoControle/Remove"
        item={item}
        onClose={handleDeleteClickConfirm}
      />
    );
  };

  const handleOpenAddControle = () => {
    ContentManager.addContent(
      <RiscoPontoControleEdit
        url="/RiscoPontoControle"
        parameters={{ riscoId: riscoId }}
        saved={saved}
        forceRefresh={true}
        afterSave={forceRefresh}
        disabled={disabled}
        handleClose={handleClose}
      />
    );
  };

  const renderButtonAddControleRiscoIA = () => [
    <Button
      id={'botaoMapearControlesRisco'}
      key={1}
      className="ml-2"
      onClick={() => handleOpenAddControle()}
      type="primary"
    >
      <LucideIcon icon="plus" />
      <span className="ml-2">
        {capitalizeFirstLetter(resources.pontosdecontrole)}
      </span>
    </Button>,
  ];

  const handleAssistenteVirtualClick = () => {
    try {
      http
        .post('/RiscoPontoControle/ObterPromptMapearControles', {
          idRisco: riscoId,
        })
        .then((response) => {
          const prompt = response.data?.prompt;

          const msgConsultoraVirtual = intl
            .formatMessage({ id: 'labelRiscos' })
            .replace('{riscos}', capitalizeFirstLetter(resources.riscos));

          ContentManager.addContent(
            <AssistenteVirtual
              user={user}
              prompt={prompt}
              title={msgConsultoraVirtual}
              actionsToolBar={renderButtonAddControleRiscoIA()}
              questionBold={intl.formatMessage({ id: 'vamosComecar' })}
              interactionType={TIPO_INTERACAO_SOPHIE.SUGERIR_CONTROLES_RISCO}
            />
          );
        });
    } catch (err) {
      errorHandler(err);
    }
  };

  const renderFarol = (item) => (
    <span>
      {item.farol && item.farol.cor && (
        <LucideIcon
          icon={item.farol.icone}
          color={item.farol.cor}
          colorHover={item.farol.cor}
          size={22}
        />
      )}
    </span>
  );

  const renderPeso = (item) => (
    <>{item?.peso != null ? `${formatPeso(item.peso)}%` : ''}</>
  );

  const renderPontoControle = (item, parameters) => (
    <a tabIndex="-1" onClick={() => handleEditClick(item, parameters)}>
      {item.pontoControle}
    </a>
  );

  const renderAlertaPesoDistribuido = (
    preventivo,
    habilitadoEfetividadePonderadaAtual
  ) => {
    if (!habilitadoEfetividadePonderadaAtual) return null;

    const pesoPreventivo = parsePeso(pesoControle?.pesoPreventivo);
    const pesoCorretivo = parsePeso(pesoControle?.pesoCorretivo);
    const peso = parsePeso(pesoControle?.peso);

    if (isLoadingPesoControle) return null;
    if (!pesoControle?.haControles) return null;

    if (
      configuracao.avaliarEfetividadeControleSeparadamente &&
      ((preventivo && pesoPreventivo != 100) ||
        (!preventivo && pesoCorretivo != 100))
    ) {
      return (
        <Row>
          <Col md={12}>
            <Alert color="warning">
              <p>
                {`${intl.formatMessage(
                  {
                    id: preventivo
                      ? 'msgValidacaoAtencaoPesoTodosControlesNaoTotalizam100Preventivo'
                      : 'msgValidacaoAtencaoPesoTodosControlesNaoTotalizam100Corretivo',
                  },
                  { pontosdecontrole: resources.pontosdecontrole }
                )} ${
                  preventivo
                    ? formatPeso(pesoPreventivo)
                    : formatPeso(pesoCorretivo)
                }%`}
              </p>
            </Alert>
          </Col>
        </Row>
      );
    } else if (
      !configuracao.avaliarEfetividadeControleSeparadamente &&
      peso != 100
    ) {
      return (
        <Row>
          <Col md={12}>
            <Alert color="warning">
              <p>
                {`${intl.formatMessage(
                  {
                    id: 'msgValidacaoAtencaoPesoTodosControlesNaoTotalizam100',
                  },
                  { pontosdecontrole: resources.pontosdecontrole }
                )} ${formatPeso(peso)}%`}
              </p>
            </Alert>
          </Col>
        </Row>
      );
    }
  };

  const allowDelete = disabled
    ? false
    : model && model.getModel('permissions').getModel('allowDelete').value;

  const hasGR = modules.find((m) => m.id === MODULOS.GESTAO_RISCO && m.exibir);
  const configuracaoRisco = model.getModel('configuracaoRisco')
    ? model.getModel('configuracaoRisco').value
    : null;

  const actionsToolBar = [
    <Fragment key={1}>
      <Button
        id={'botaoMapearControlesRisco'}
        className="ml-2"
        onClick={handleAssistenteVirtualClick}
        type="primary"
      >
        <SophieIcon color="#fff" size={15} />
        <span className="ml-2">
          {intl.formatMessage(
            { id: 'mapearControles' },
            {
              pontosdecontrole: capitalizeFirstLetter(
                resources.pontosdecontrole
              ),
            }
          )}
        </span>
      </Button>
      <UncontrolledTooltip
        placement="right"
        target={'botaoMapearControlesRisco'}
      >
        {intl.formatMessage(
          {
            id: 'labelSophieToolTipMapearControlesRisco',
          },
          {
            riscos: resources.riscos,
          }
        )}
      </UncontrolledTooltip>
    </Fragment>,
  ];

  return (
    <>
      <Row>
        {hasGR && (
          <Col md={12}>
            {configuracaoRisco?.exibirDiagramaBowTie && (
              <PanelCollapse
                open={true}
                header={capitalizeFirstLetter(
                  intl.formatMessage(
                    { id: 'labelBowTie' },
                    { risco: resources.risco }
                  )
                )}
              >
                <GraficoBowTieView
                  riscoId={riscoId}
                  saved={saved}
                  disabled={disabled}
                  onControlSaved={forceRefresh}
                />
              </PanelCollapse>
            )}
          </Col>
        )}
      </Row>
      <Row>
        {configuracaoRisco?.utilizarCausaRisco && (
          <Col md={6}>
            <PanelCollapse
              open={true}
              header={capitalizeFirstLetter(
                intl.formatMessage(
                  { id: 'labelCausasDoRisco' },
                  {
                    pontosproblematicos: resources.pontosproblematicos,
                    risco: resources.risco,
                  }
                )
              )}
            >
              <CausasRisco
                refreshCount={forceRefresh}
                model={causasRisco}
                riscoId={riscoId}
                configuracao={configuracaoRiscoProp}
                pageSize={999}
                refreshAfterRemove={saved}
                disabled={disabled}
              />
            </PanelCollapse>
          </Col>
        )}
        {configuracaoRisco?.utilizarConsequenciaRisco && (
          <Col md={6}>
            <PanelCollapse
              open={true}
              header={capitalizeFirstLetter(
                intl.formatMessage(
                  { id: 'labelConsequenciasDoRisco' },
                  {
                    consequencias: resources.consequencias,
                    risco: resources.risco,
                  }
                )
              )}
            >
              <ConsequenciasRisco
                refreshCount={forceRefresh}
                model={consequenciasRisco}
                riscoId={riscoId}
                configuracao={configuracaoRiscoProp}
                pageSize={999}
                refreshAfterRemove={saved}
                disabled={disabled}
              />
            </PanelCollapse>
          </Col>
        )}
      </Row>

      {hasGR && (
        <PanelCollapse
          header={capitalizeFirstLetter(resources.pontosdecontrole)}
        >
          {configuracao.avaliarEfetividadeControleSeparadamente &&
            renderAlertaPesoDistribuido(true, pesoHabilitado)}
          {renderAlertaPesoDistribuido(false, pesoHabilitado)}
          <LoadingContainer isLoading={isLoading}>
            {showEfetividadeControlePanel && (
              <EfetividadeControle
                model={modelOrigem}
                modelCorretivo={modelCorretivo}
                modelPreventivo={modelPreventivo}
                disabled={disabledEfetividadeControlePanel}
                configuracaoAvaliacao={configuracaoAvaliacao}
                required={requiredEfetividadeControlePanel}
                efetividadeAutomatica={efetividadeAutomatica}
                riscoId={riscoId}
              />
            )}
            {!isLoading && (
              <List
                url="/RiscoPontoControle"
                tipoItem={6}
                parameters={{ riscoId: riscoId }}
                editComponent={RiscoPontoControleEdit}
                showPage
                pageSize={pageSize ? pageSize : 20}
                showExport={false}
                disabled={disabled ? disabled : false}
                showDelete={allowDelete}
                showPaginacaoSimples={showPaginacaoSimples}
                modelOrigem={modelOrigem}
                showNew={disabled ? false : true}
                saved={saved}
                tag={COLUNAS.RISCO_PONTO_CONTROLE}
                hierarchy={true}
                showPagination={true}
                retirarTreeCamposCustomizados={true}
                showFilter={false}
                showSearch={false}
                showQuickSearch={true}
                showMenu={true}
                onDeleting={handleDelete}
                forceRefresh={true}
                actionsToolBarEnd={actionsToolBar}
                hideInsertDropDown={true}
                afterSave={() => forceRefresh()}
              >
                <ListColumn
                  headerText={intl.formatMessage({ id: 'idIntegracao' })}
                  default
                  valueField="idIntegracao"
                  sortField="IdIntegracao"
                />
                <ListColumn
                  headerText={capitalizeFirstLetter(resources.pontodecontrole)}
                  tree={true}
                  default
                  valueField="pontoControle"
                  required
                  valueFunction={renderPontoControle}
                />
                <ListColumn
                  headerText={capitalizeFirstLetter(
                    intl.formatMessage({ id: 'observacao' })
                  )}
                  valueField="descricao"
                />
                <ListColumn
                  headerText={capitalizeFirstLetter(
                    intl.formatMessage({ id: 'periodicidade' })
                  )}
                  valueField="periodicidade"
                />
                <ListColumn
                  headerText={capitalizeFirstLetter(
                    intl.formatMessage(
                      { id: 'responsavel' },
                      { responsavel: resources.responsavel }
                    )
                  )}
                  valueField="responsavel"
                />
                <ListColumn
                  headerText={capitalizeFirstLetter(
                    intl.formatMessage({ id: 'status' })
                  )}
                  valueField="status"
                />
                <ListColumn
                  headerText={capitalizeFirstLetter(resources.farol)}
                  valueField="farol"
                  valueFunction={renderFarol}
                />
                <ListColumn
                  headerText={capitalizeFirstLetter(
                    intl.formatMessage({ id: 'tolerancia' })
                  )}
                  valueField="diasToleranciaRevisao"
                  sortField="DiasToleranciaRevisao"
                />
                <ListColumn
                  headerText={capitalizeFirstLetter(
                    intl.formatMessage({ id: 'tipoTolerancia' })
                  )}
                  valueField="tipoTolerancia"
                  sortField="TipoTolerancia.Descricao"
                />
                <ListColumn
                  headerText={capitalizeFirstLetter(
                    intl.formatMessage({
                      id: 'numeroRevisoesGeradasAutomaticamente',
                    })
                  )}
                  valueField="numeroRevisoesGeradasAutomaticamente"
                  sortField="NumeroRevisoesGeradasAutomaticamente"
                />
                {configuracao &&
                  configuracao.avaliarEfetividadeControleSeparadamente && (
                    <ListColumn
                      headerText={capitalizeFirstLetter(
                        intl.formatMessage(
                          { id: 'funcaoPontoControle' },
                          { pontodecontrole: resources.pontodecontrole }
                        )
                      )}
                      valueField="funcaoPontoControle"
                    />
                  )}
                <ListColumn
                  headerText={capitalizeFirstLetter(
                    intl.formatMessage({ id: 'label.frequencia' })
                  )}
                  valueField="frequenciaControle"
                  sortField="FrequenciaControle"
                />
                <ListColumn
                  visible={
                    configuracao &&
                    configuracao.avaliarEfetividadeControle &&
                    configuracao.avaliarEfetividadeControleIndividualmente
                  }
                  headerText={capitalizeFirstLetter(
                    intl.formatMessage(
                      { id: 'efetividadeControle' },
                      { pontodecontrole: resources.pontodecontrole }
                    )
                  )}
                  valueField="ultimaEfetividade"
                />
                <ListColumn
                  headerText={`${capitalizeFirstLetter(
                    intl.formatMessage({ id: 'peso' })
                  )} (%)`}
                  visible={habilitadoEfetividadePonderada && pesoHabilitado}
                  valueField="peso"
                  valueFunction={renderPeso}
                  sortField="peso"
                />
              </List>
            )}
            <Legenda tipoFarol={1} />
          </LoadingContainer>
        </PanelCollapse>
      )}
    </>
  );
};

export default RiscoPontoControleList;
